import * as React from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import { useNavigate } from "react-router-dom";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Paper from "@mui/material/Paper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PublicIcon from "@mui/icons-material/Public";
import { alpha, styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ReactComponent as UploadIcon } from "../../../../images/uploadIcon.svg";
import { ReactComponent as EmailIcon } from "../../../../images/EmailIcon.svg";
import FilesUploadPopup from "./FilesUploadPopup";
import WebsiteAnalysisDrawer from "./WebsiteAnalysisDrawer";

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  border: "1px solid  #C3C3C3",
  borderRadius: "10px",
  boxShadow: "none",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  padding: theme.spacing(2.0),
  transition: theme.transitions.create(["box-shadow", "background-color"], {
    duration: theme.transitions.duration.short,
  }),
  "&:hover": {
    boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.15)}`,
    backgroundColor: "#FAFAFA",
  },
}));

function AddSourceDrawer({ open, onClose, onFileUpload }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [openEmailDrawer, setOpenEmailDrawer] = React.useState(false);
  const [openUploadDrawer, setOpenUploadDrawer] = React.useState(false);
  const [openWebsiteDrawer, setOpenWebsiteDrawer] = React.useState(false);

  const handleBackToAddSource = (setDrawerState) => {
    setDrawerState(false);
    setTimeout(() => {
      setOpenEmailDrawer(false);
      setOpenUploadDrawer(false);
      setOpenWebsiteDrawer(false);
      // setOpen(true);
    }, 300);
  };

  return (
    <>
      {/* Main Add Source Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        variant="temporary"
        slotProps={{
          paper: {
            sx: {
              width: isMobile ? "100%" : 650,
              maxWidth: "100%",
              p: 3,
              boxSizing: "border-box",
              borderTopLeftRadius: theme.shape.borderRadius,
              borderBottomLeftRadius: theme.shape.borderRadius,
            },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              onClick={onClose}
              edge="start"
              aria-label="back"
              sx={{ color: "black" }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: "bold", ml: 1 }}>
              Add Source
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Email Conversations */}
          <StyledPaper
            elevation={0}
            onClick={() => {
              onClose();
              setTimeout(() => setOpenEmailDrawer(true), 300);
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
              <EmailIcon />
              <Typography variant="subtitle1" sx={{ ml: 1 }}>
                Email Conversations
              </Typography>
            </Box>
            <Typography variant="body2" color="#8F8F8F">
              Easily Upload email threads in EML, Mbox, or MSG format for
              analysis
            </Typography>
          </StyledPaper>

          {/* Upload Files */}
          <StyledPaper
            elevation={0}
            onClick={() => {
              onClose();
              setTimeout(() => setOpenUploadDrawer(true), 300);
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
              <UploadIcon />
              <Typography variant="subtitle1" sx={{ ml: 1 }}>
                Upload Files
              </Typography>
            </Box>
            <Typography variant="body2" color="#8F8F8F">
              {/* Upload PDF, Images, Docs, Audio, or Video files for instant analysis */}
              Upload PDF, Images, Docs files for instant analysis
            </Typography>
          </StyledPaper>

          {/* Website Analysis */}
          <StyledPaper
            elevation={0}
            onClick={() => {
              onClose();
              setTimeout(() => setOpenWebsiteDrawer(true), 300);
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
              <PublicIcon color="primary" sx={{ fontSize: 32, mr: 1.5 }} />
              <Typography variant="subtitle1">Website Analysis</Typography>
            </Box>
            <Typography variant="body2" color="#8F8F8F">
              Get simple reports on your site's speed and reliability
            </Typography>
          </StyledPaper>
        </Box>
      </Drawer>

      {/* Email Conversations Drawer */}
      <Drawer
        anchor="right"
        open={openEmailDrawer}
        onClose={() => setOpenEmailDrawer(false)}
        slotProps={{
          paper: {
            sx: {
              width: isMobile ? "100%" : 600,
              maxWidth: "100%",
              p: 0,
              boxSizing: "border-box",
            },
          },
        }}
      >
        <FilesUploadPopup
          title="Email Conversations"
          description="Easily Upload email threads in EML, Mbox, or MSG format for analysis"
          accept=".eml,.msg,.mbox"
          onClose={() => setOpenEmailDrawer(false)}
          onFileUpload={onFileUpload}
          onBack={() => handleBackToAddSource(setOpenEmailDrawer)}
          useNavigateBack={true}
        />
      </Drawer>

      {/* Upload Files Drawer */}
      <Drawer
        anchor="right"
        open={openUploadDrawer}
        onClose={() => setOpenUploadDrawer(false)}
        slotProps={{
          paper: {
            sx: {
              width: isMobile ? "100%" : 600,
              maxWidth: "100%",
              p: 0,
              boxSizing: "border-box",
            },
          },
        }}
      >
        <FilesUploadPopup
          title="Upload Files"
          description="Upload PDF, Images, Docs, Audio, or Video files for instant analysis"
          accept=".docx,.pdf,.png,.jpeg,.jpg,.mp4,.mp3,.mov,.avi,.mkv,.eml,.msg,.mbox"
          onClose={() => setOpenUploadDrawer(false)}
          onFileUpload={onFileUpload}
          onBack={() => handleBackToAddSource(setOpenUploadDrawer)}
        />
      </Drawer>

      {/* Website Analysis Drawer */}
      <Drawer
        anchor="right"
        open={openWebsiteDrawer}
        onClose={() => setOpenWebsiteDrawer(false)}
        slotProps={{
          paper: {
            sx: {
              width: isMobile ? "100%" : 600,
              maxWidth: "100%",
              p: 0,
              boxSizing: "border-box",
            },
          },
        }}
      >
        <WebsiteAnalysisDrawer
          onClose={() => setOpenWebsiteDrawer(false)}
          onBack={() => handleBackToAddSource(setOpenWebsiteDrawer)}
          useNavigateBack={false}
        />
      </Drawer>
    </>
  );
}

export default AddSourceDrawer;
